import appendQueryParams  from '../../utils/appendQueryParams';
import AbstractConnector, {
	RequestOptions,
	SaveOptions,
	ListOptions,
	Response,
}                          from '../../core/AbstractConnector';
import qs                 from 'qs';

const MAX_PER_PAGE = 300;
const MAX_ID_FILTER_CHUNK = 100;

export default abstract class AbstractApiPlatformConnector extends AbstractConnector {
	abstract getListData(data: Record<string, unknown>): Response['data'];

	/* -------------------------------------------------------------------------- */
	/*                                   FETCH                                    */

	/* -------------------------------------------------------------------------- */

	async fetch(path: string, options: RequestOptions) {
		const response = await this._request(path, options);

		return {
			status: response.status,
			data: response.data,
			headers: response.headers,
		};
	}

	/* -------------------------------------------------------------------------- */
	/*                                    LIST                                    */

	/* -------------------------------------------------------------------------- */

	async list(path: string, options: ListOptions = {}) {
		const { filters } = options;

		if (filters) {
			const chunkableFilter = this._detectChunkableFilter(filters);

			if (chunkableFilter) {
				return this._listWithChunkedFilter(path, options, chunkableFilter);
			}
		}

		// Cas 2 : pagination simple
		return this._listPaginated(path, options);
	}

	/* -------------------------------------------------------------------------- */
	/*                              PAGINATION AUTO                               */

	/* -------------------------------------------------------------------------- */

	private async _listPaginated(path: string, options: ListOptions) {
		const allData: unknown[] = [];
		let page = 1;
		let hasMore = true;
		let warnedForMultiPage = false;
		let lastHeaders = new Headers();

		while (hasMore) {
			const queryPath = this._buildListPath(path, {
				...options,
				page,
			});

			const response = await this._request(queryPath, {
				headers: {
					...(options.locales ? { 'X-LOCALE': options.locales } : {}),
					...options.headers,
				},
				requestReportStore: options?.requestReportStore,
			});

			lastHeaders = response.headers;

			const data = this.getListData(response.data) as any[] || [];

			allData.push(...data);

			// INFO — chargement multi-pages déclenché
			if (!warnedForMultiPage && data.length === MAX_PER_PAGE) {
				warnedForMultiPage = true;

				console.info('[API LIST] Multi-page loading triggered', {
					reason: 'RESULT_LIMIT_EXCEEDED',
					path,
					maxPerPage: MAX_PER_PAGE,
					currentPage: page,
					filters: options.filters,
					sorts: options.sorts,
				});
			}

			// Dernière page si moins que la limite backend
			if (data.length < MAX_PER_PAGE) {
				hasMore = false;
			} else {
				page++;
			}
		}

		return {
			status: 200,
			data: allData,
			headers: lastHeaders,
		};
	}

	/* -------------------------------------------------------------------------- */
	/*                          FILTRES ID DÉCOUPÉS                               */

	/* -------------------------------------------------------------------------- */

	private async _listWithChunkedFilter(
		path: string,
		options: ListOptions,
		chunked: { key: string; values: unknown[] },
	) {
		const { key, values } = chunked;
		const chunks = this._chunkArray(values, MAX_ID_FILTER_CHUNK);

		console.info('[API LIST] Filter split into multiple requests', {
			reason: 'ARRAY_FILTER_TOO_LARGE',
			path,
			filterKey: key,
			totalValues: values.length,
			maxPerRequest: MAX_ID_FILTER_CHUNK,
			chunksCount: chunks.length,
			chunkSizes: chunks.map(c => c.length),
			otherFilters: Object.keys(options.filters || {}).filter(k => k !== key),
		});

		const allResults: any[] = [];

		if (chunks.length === 0) return { status: 200, data: [], headers: new Headers() };

		// On exécute le premier chunk **séquentiellement**
		const firstResponse = await this._listPaginated(path, {
			...options,
			filters: { ...options.filters, [key]: chunks[0] } as any,
		});
		allResults.push(...firstResponse.data);

		if (chunks.length > 1) {
			// Les chunks restants sont exécutés **en parallèle**
			const restResponses = await Promise.all(
				chunks.slice(1).map(chunk =>
					this._listPaginated(path, {
						...options,
						filters: { ...options.filters, [key]: chunk } as any,
					}),
				),
			);

			restResponses.forEach(r => allResults.push(...r.data));
		}

		// Déduplication
		const uniqueResults = Array.from(
			new Map(allResults.map(item => [item.id, item])).values(),
		);

		return {
			status: 200,
			data: uniqueResults,
			headers: firstResponse.headers,
		};
	}

	/* -------------------------------------------------------------------------- */
	/*                               SAVE / UPDATE                                */

	/* -------------------------------------------------------------------------- */

	public async save(
		path: string,
		attributes: ModelAttributes,
		{ headers, ...options }: RequestOptions = {},
		saveOptions: SaveOptions = {},
	) {
		const finalPath = appendQueryParams(path, saveOptions.params);
		const shouldStringify = saveOptions.stringifyAttributes === true;

		const bodyStr = shouldStringify
			? qs.stringify(attributes)
			: JSON.stringify(attributes);

		const response = await this._request(finalPath, {
			...options,
			method: options.method ?? 'POST',
			body: bodyStr,
			headers: shouldStringify
				? {
					...headers,
					'Content-Type': 'application/x-www-form-urlencoded',
				}
				: headers,
		});

		return {
			status: response.status,
			data: response.data,
			headers: response.headers,
		};
	}

	public async destroy(path: string) {
		const response = await this._request(path, { method: 'DELETE' });

		return {
			status: response.status,
			data: response.data,
			headers: response.headers,
		};
	}

	/* -------------------------------------------------------------------------- */
	/*                              REQUEST BAS NIVEAU                            */

	/* -------------------------------------------------------------------------- */

	private async _request(path: string, options: RequestOptions = {}) {
		const start = performance.now();

		options.headers = options.headers || {};

		await this._emitRequestStart(path, options);

		const defaultHeaders = {
			'Accept': 'application/ld+json',
			'Content-Type': 'application/ld+json',
			'X-Include-Files': 'true',
		};

		const headers = new Headers({
			...defaultHeaders,
			...(options.headers || {}),
		});

		const fetchOptions = { ...options, headers };

		const nativeResponse = await fetch(path, fetchOptions);

		const json: Record<string, unknown> = await nativeResponse.json().catch(() => null);

		const error =
			(json &&
				(json.error ??
					json.message ??
					json.detail ??
					json['hydra:description'])) ??
			nativeResponse.statusText ??
			null;

		const duration = Number((performance.now() - start).toFixed(2));

		if (typeof window === 'undefined') { // Seulement côté serveur
			options.requestReportStore?.addRequest({
				startedAt: start,
				status: nativeResponse.status,
				url: path,
				duration,
			});
		}

		if (!options?.noErrorCatch && nativeResponse.status >= 400 && nativeResponse.status <= 599) {
			if (!options?.silentError) await this._emitError(error, path);
			throw error;
		}

		return {
			error,
			data: json || {},
			status: nativeResponse.status,
			headers: nativeResponse.headers,
		};
	}

	/* -------------------------------------------------------------------------- */
	/*                               HELPERS PRIVÉS                               */

	/* -------------------------------------------------------------------------- */

	private _buildListPath(
		path: string,
		options: ListOptions & { page?: number },
	) {
		const params: Record<string, unknown> = {};

		if (options.filters) {
			Object.entries(options.filters).forEach(([key, value]) => {
				let v: any = value;

				// Check itemsPerPage
				if (key === 'itemsPerPage' && (Number(value) > MAX_PER_PAGE)) {
					v = MAX_PER_PAGE;
				}

				params[key] = v;
			});
		}

		if (options.sorts?.length) {
			Object.entries(options.sorts[0]).forEach(([field, way]) => {
				params[`order[${field}]`] = way;
			});
		}

		if (options.page) {
			params['page'] = String(options.page);
		}

		const queryString = qs.stringify(params, { encode: false, arrayFormat: 'brackets' });
		return queryString ? `${path}?${queryString}` : path;
	}

	private _chunkArray<T>(array: T[], size: number): T[][] {
		const chunks: T[][] = [];
		for (let i = 0; i < array.length; i += size) {
			chunks.push(array.slice(i, i + size));
		}
		return chunks;
	}

	private _detectChunkableFilter(filters: Record<string, unknown>) {
		const arrayFilters = Object.entries(filters)
			.filter(([, value]) => Array.isArray(value));

		const oversizedFilters = arrayFilters.filter(
			([, value]) => (value as unknown[]).length > MAX_ID_FILTER_CHUNK,
		);

		if (oversizedFilters.length > 1) {
			throw new Error(
				`Multiple array filters exceed max size (${MAX_ID_FILTER_CHUNK}): ${oversizedFilters
					.map(([key]) => key)
					.join(', ')}`,
			);
		}

		if (oversizedFilters.length === 1) {
			const [key, value] = oversizedFilters[0];
			return {
				key,
				values: value as unknown[],
			};
		}

		return null;
	}
}
